import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  MonitorHeart as StatusIcon,
  Insights as UsageIcon
} from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import integrationService from '../services/integrationService'
import HealthStatusCard from '../components/integrations/HealthStatusCard'
import IntegrationUsagePanel from '../components/integrations/IntegrationUsagePanel'

type MonitorTab = 'status' | 'usage'

/** One place for integration health (Status) and API usage (Usage). */
const IntegrationMonitorPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: MonitorTab = searchParams.get('tab') === 'usage' ? 'usage' : 'status'

  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['integration-health'],
    queryFn: () => integrationService.getAllHealth(),
    refetchInterval: 60000,
    staleTime: 30000
  })

  const handleTabChange = (_event: React.SyntheticEvent, value: MonitorTab) => {
    setSearchParams(value === 'status' ? {} : { tab: value }, { replace: true })
  }

  const integrations = data?.integrations || []
  const totalCount = data?.total || 0
  const healthyCount = data?.healthy_count || 0
  const errorCount = data?.error_count || 0

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" component="h1">
            Integration Monitor
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Health of every connected service, and how much each one is used
          </Typography>
        </Box>
        <Tooltip title="Refresh health status">
          <IconButton onClick={() => refetch()} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Services
              </Typography>
              <Typography variant="h4" component="div">
                {isLoading ? '–' : totalCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Healthy
              </Typography>
              <Typography variant="h4" component="div" color="success.main">
                {isLoading ? '–' : healthyCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Needs attention
              </Typography>
              <Typography variant="h4" component="div" color={errorCount > 0 ? 'error.main' : 'text.primary'}>
                {isLoading ? '–' : errorCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab value="status" icon={<StatusIcon />} label="Status" iconPosition="start" />
          <Tab value="usage" icon={<UsageIcon />} label="Usage" iconPosition="start" />
        </Tabs>
      </Box>

      {tab === 'status' && (
        <>
          {isLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
              <CircularProgress />
            </Box>
          )}
          {error && (
            <Alert severity="error">Failed to load integration health data. Please try again.</Alert>
          )}
          {!isLoading && !error && (
            <>
              <Box mb={2}>
                <Alert severity="info" icon={<RefreshIcon />}>
                  Refreshes every 60 seconds. Last updated{' '}
                  {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString() : '–'}.
                </Alert>
              </Box>
              <Grid container spacing={3}>
                {integrations.map((integration) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={`${integration.service_type}-${integration.provider_name}`}
                  >
                    <HealthStatusCard
                      integration={integration}
                      integrationId={integration.service_type + '-' + integration.provider_name}
                      onCheckComplete={() => refetch()}
                    />
                  </Grid>
                ))}
              </Grid>
              {integrations.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Typography variant="h6" color="textSecondary">
                    No integrations configured
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    Configure integrations to see their health status here
                  </Typography>
                </Box>
              )}
            </>
          )}
        </>
      )}

      {tab === 'usage' && <IntegrationUsagePanel />}
    </Box>
  )
}

export default IntegrationMonitorPage
